import type { ModalQueueGeneratedOpener } from '@carvana/merch-react-hooks';
import { useDebouncedCallback } from '@carvana/merch-react-hooks';
import { useCallback } from 'react';
import {
  useIsLoggedIn,
  useIsUserStateLoaded
} from '~/context/common/UserContext';
import useIsInAppraisalModalExperiment from '~/hooks/common/experiments/useIsInAppraisalModalExperiment';
import useTrackAppraisalCtaRendered from './useTrackAppraisalCtaRendered';

type TradeCtaClick = {
  isEligible: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
};

export default function useTradeCtaClick(
  setAppraisalEmbeddedModalOpen: ModalQueueGeneratedOpener
): TradeCtaClick {
  const isLoggedIn = useIsLoggedIn();
  const isUserStateLoaded = useIsUserStateLoaded();
  const isInExperiment = useIsInAppraisalModalExperiment();
  const isEligible = isUserStateLoaded && isLoggedIn && isInExperiment;
  const trackAppraisalCtaRendered = useTrackAppraisalCtaRendered();

  const openModal = useCallback<React.MouseEventHandler<HTMLElement>>(() => {
    trackAppraisalCtaRendered();

    if (!isEligible) return;

    setAppraisalEmbeddedModalOpen(true);
  }, [isEligible, setAppraisalEmbeddedModalOpen, trackAppraisalCtaRendered]);

  const onClick = useDebouncedCallback(openModal, [openModal], 500, {
    leading: true,
    onEveryCall: ev => {
      if (isEligible) ev.preventDefault();
    },
    trailing: false
  });

  return { isEligible, onClick };
}
